import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  Upload,
  FileText,
  Send,
  X,
  CheckCircle2,
  AlertCircle,
  GitCompare,
  ArrowLeft,
  Download,
  RotateCcw,
  Trash2
} from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { Button } from '@foundation/components/ui/button';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card, CardContent, CardHeader, CardTitle } from '@foundation/components/ui/card';
import { Input } from '@foundation/components/ui/input';
import { Textarea } from '@foundation/components/ui/textarea';
import { Label } from '@foundation/components/ui/label';
import { Progress } from '@foundation/components/ui/progress';
import { Badge } from '@foundation/components/ui/badge';
import { Alert, AlertDescription } from '@foundation/components/ui/alert';
import { useToast } from '@foundation/hooks/use-toast';
import { useUser } from '@foundation/contextapi/UserContext';
import { offlineSupabase } from '@foundation/utils/offlineSupabaseProxy';
import { enhancedClientService } from '../services/enhancedClientService';
import ScopingAILayout from '../components/RevisrLayout';
import {
  createRagStore,
  uploadToRagStore,
  queryDocuments,
  deleteRagStore,
  listFilesInStore,
  compareMultipleStandards,
  compareMultipleStandardsStream,
} from '../services/quickCompareService';
import type {
  QuickCompareProgress,
  RAGService,
} from '../types/quickCompare';
import { ALLOWED_FILE_TYPES, MAX_FILE_SIZE } from '../lib/constants';

type AppStatus = 'file-selection' | 'upload' | 'processing' | 'chatting';

const QuickCompare: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useUser();

  // Get orgId using the same approach as foundation (RPC function)
  const [orgId, setOrgId] = useState<string | undefined>(undefined);

  useEffect(() => {
    const getOrgId = async () => {
      if (!user?.id) return;

      try {
        // Use the same RPC function as foundation settings page
        const { data, error } = await offlineSupabase
          .rpc("get_user_organizations", { p_user_id: user.id });

        if (error) {
          console.warn('Could not get organizations via RPC:', error);
          // Fallback: Try using enhancedClientService
          try {
            const orgIds = await enhancedClientService.getUserOrganizations();
            if (orgIds && orgIds.length > 0) {
              setOrgId(orgIds[0]);
              console.log('✅ Got orgId from enhancedClientService fallback:', orgIds[0]);
              return;
            }
          } catch (fallbackErr) {
            console.warn('Fallback method also failed:', fallbackErr);
          }
          return;
        }

        // Get the first organization (default workspace)
        if (data && data.length > 0) {
          const firstOrg = data[0];
          setOrgId(firstOrg.id);
          console.log('✅ Got orgId from get_user_organizations:', firstOrg.id);
        } else {
          // No organizations found via RPC, try fallback
          console.warn('No organizations found via RPC, trying fallback...');
          try {
            const orgIds = await enhancedClientService.getUserOrganizations();
            if (orgIds && orgIds.length > 0) {
              setOrgId(orgIds[0]);
              console.log('✅ Got orgId from enhancedClientService fallback:', orgIds[0]);
            }
          } catch (fallbackErr) {
            console.warn('Fallback method also failed:', fallbackErr);
          }
        }
      } catch (err) {
        console.warn('Could not get orgId:', err);
        // Try fallback as last resort
        try {
          const orgIds = await enhancedClientService.getUserOrganizations();
          if (orgIds && orgIds.length > 0) {
            setOrgId(orgIds[0]);
            console.log('✅ Got orgId from enhancedClientService fallback (catch block):', orgIds[0]);
          }
        } catch (fallbackErr) {
          console.warn('All methods to get orgId failed:', fallbackErr);
        }
      }
    };

    getOrgId();
  }, [user?.id]);

  // Helper function to get localStorage key with user email
  const getStoreIdKey = (): string => {
    const userEmail = user?.email || 'anonymous';
    const sanitizedEmail = userEmail.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
    return `quickCompare_storeId_${ragService}_${sanitizedEmail}`;
  };

  // Helper function to get localStorage key for output
  const getOutputKey = (): string => {
    const userEmail = user?.email || 'anonymous';
    const sanitizedEmail = userEmail.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
    return `quickCompare_output_${ragService}_${sanitizedEmail}`;
  };

  // Helper function to get localStorage key for status
  const getStatusKey = (): string => {
    const userEmail = user?.email || 'anonymous';
    const sanitizedEmail = userEmail.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
    return `quickCompare_status_${ragService}_${sanitizedEmail}`;
  };

  // Helper function to get localStorage key for RAG service
  const getRagServiceKey = (): string => {
    const userEmail = user?.email || 'anonymous';
    const sanitizedEmail = userEmail.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
    return `quickCompare_ragService_${sanitizedEmail}`;
  };

  // Helper function to get store name with user email
  const getStoreName = (): string => {
    const userEmail = user?.email || 'anonymous';
    const sanitizedEmail = userEmail.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
    return `quick-compare-persistent-${ragService}-${sanitizedEmail}`;
  };

  // Initialize state from localStorage if available
  // Note: We don't restore 'processing' status since we can't resume processing
  const [status, setStatus] = useState<AppStatus>(() => {
    // Always start with 'upload' view to allow users to upload files or start new audits
    // Previous results can be viewed by running a new audit or clicking "View Results"
    if (typeof window !== 'undefined' && user?.email) {
      const userEmail = user.email;
      const sanitizedEmail = userEmail.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();

      // Try to get the saved RAG service first
      const savedRagService = (localStorage.getItem(`quickCompare_ragService_${sanitizedEmail}`) as RAGService) || 'gemini';

      const savedStatus = localStorage.getItem(`quickCompare_status_${savedRagService}_${sanitizedEmail}`);
      // Clear any stuck 'processing' status
      if (savedStatus === 'processing') {
        localStorage.removeItem(`quickCompare_status_${savedRagService}_${sanitizedEmail}`);
        const savedOutput = localStorage.getItem(`quickCompare_output_${savedRagService}_${sanitizedEmail}`);
        if (savedOutput) {
          localStorage.removeItem(`quickCompare_output_${savedRagService}_${sanitizedEmail}`);
        }
      }
    }
    // Always return 'upload' to show upload interface, even if previous results exist
    return 'upload';
  });

  const [policyFile, setPolicyFile] = useState<File | null>(null);
  const [standardFile, setStandardFile] = useState<File | null>(null);
  const [storeId, setStoreId] = useState<string | null>(() => {
    if (typeof window !== 'undefined' && user?.email) {
      const userEmail = user.email;
      const sanitizedEmail = userEmail.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();

      // Try to get the saved RAG service first
      const savedRagService = (localStorage.getItem(`quickCompare_ragService_${sanitizedEmail}`) as RAGService) || 'gemini';

      const savedStoreId = localStorage.getItem(`quickCompare_storeId_${savedRagService}_${sanitizedEmail}`);
      return savedStoreId || null;
    }
    return null;
  });
  const [progress, setProgress] = useState<QuickCompareProgress | null>(null);
  const [query, setQuery] = useState('');
  const [isQueryLoading, setIsQueryLoading] = useState(false);
  const [output, setOutput] = useState<string>(() => {
    if (!user?.email) return '';
    const sanitizedEmail = user.email.replace(/[^a-zA-Z0-9]/g, '_');
    try {
      // We need to access ragService from localStorage since it's not available in the initial state function
      // Or we can just use a generic key for output if it's not provider-specific
      // But ideally we should separate output by provider too
      // For now let's try to read the ragService from localStorage
      const userEmail = user.email;
      const sanitizedEmail = userEmail.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
      const savedRagService = localStorage.getItem(`quickCompare_ragService_${sanitizedEmail}`) || 'knowledge';

      const savedOutput = localStorage.getItem(`quickCompare_output_${savedRagService}_${sanitizedEmail}`);
      return savedOutput || '';
    } catch (e) {
      return '';
    }
  });

  // Streaming state
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingProgress, setStreamingProgress] = useState<{ step: string; message: string }>({ step: '', message: '' });
  const [interimFindings, setInterimFindings] = useState<any[]>([]);
  const interimFindingsScrollRef = useRef<HTMLDivElement>(null);
  const [isDraggingPolicy, setIsDraggingPolicy] = useState(false);
  const [isDraggingStandard, setIsDraggingStandard] = useState(false);
  const [showPromptSettings, setShowPromptSettings] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ name: string; category: 'policy' | 'standard' }>>([]);
  const [selectedPolicyFile, setSelectedPolicyFile] = useState<string | null>(null);
  const [selectedStandardFiles, setSelectedStandardFiles] = useState<string[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isCheckingStore, setIsCheckingStore] = useState(true);
  const [ragService, setRagService] = useState<RAGService>(() => {
    if (typeof window !== 'undefined' && user?.email) {
      const saved = localStorage.getItem(getRagServiceKey());
      return (saved as RAGService) || 'gemini';
    }
    return 'gemini';
  });
  // Default system prompt - used for initial state and reset
  const defaultSystemPrompt = `You are a professional Policy Auditor and Compliance Specialist. Your task is to assess whether a POLICY document complies with the requirements specified in a STANDARD document.

CRITICAL: DOCUMENT IDENTIFICATION
- Documents labeled [POLICY DOCUMENT] are the policy documents being audited
- Documents labeled [STANDARD DOCUMENT] are the standards/requirements against which the policy is being evaluated
- You MUST compare the POLICY against the STANDARD - NOT the standard against itself
- The score should reflect how well the POLICY meets the STANDARD's requirements

ROLE AND OBJECTIVE:
- Act as a policy auditor reviewing the POLICY document against the STANDARD document
- Identify ONLY major and minor compliance gaps, missing requirements, and areas that need revision in the POLICY
- Focus on substantive issues that add real value - exclude minor rewording or stylistic changes
- Provide specific, actionable recommendations for POLICY changes to meet STANDARD requirements
- Calculate a comprehensive compliance score showing how well the POLICY complies with the STANDARD

CRITICAL FILTERING RULES:
1. ONLY include findings that represent MAJOR or MINOR compliance issues
2. EXCLUDE findings that are:
   - Just rewording of existing text without adding substantive value
   - Minor stylistic or grammatical changes
   - Cosmetic improvements that don't address compliance gaps
   - Text that already substantially meets the requirement (even if wording differs slightly)
3. INCLUDE findings that:
   - Represent missing critical requirements
   - Show non-compliance with specific standard requirements
   - Require substantive changes to meet compliance
   - Address gaps that could lead to compliance failures

OUTPUT FORMAT - CRITICAL:
You MUST respond with a valid JSON object (not an array). The response must include findings, scoring, and a final score.

JSON Structure:
{
  "findings": [
    {
      "originalText": "exact text from policy document that needs to be changed (or empty string if text doesn't exist)",
      "revisedText": "suggested revised or new text that complies with the standard",
      "reference": "specific reference to the standard requirement (section, clause, or page number)",
      "complianceStatus": "non-compliant" | "missing" | "needs-clarification",
      "severity": "critical" | "high" | "medium" | "low",
      "findingType": "major" | "minor",
      "explanation": "brief explanation of why this change is needed"
    }
  ],
  "score": {
    "totalRequirements": number,
    "compliantRequirements": number,
    "nonCompliantRequirements": number,
    "missingRequirements": number,
    "needsClarification": number,
    "scoreBreakdown": {
      "critical": number,
      "high": number,
      "medium": number,
      "low": number
    },
    "finalScore": number,
    "scoreExplanation": "Brief explanation of how the score was calculated"
  },
  "hasFindings": boolean,
  "message": "Summary message - either 'No findings. Policy is fully compliant.' or a brief summary of key issues"
}

SCORING METHODOLOGY:
1. Start with a base score of 100
2. Deduct points based on findings:
   - Critical finding: -10 points each
   - High severity finding: -5 points each
   - Medium severity finding: -2 points each
   - Low severity finding: -1 point each
3. Calculate: finalScore = max(0, 100 - (critical * 10 + high * 5 + medium * 2 + low * 1))
4. If no findings exist, finalScore = 100

RULES FOR OUTPUT:
1. If findings exist:
   - Populate the "findings" array with ONLY major and minor compliance issues (exclude rewording without value)
   - Each finding MUST include "findingType": "major" or "minor"
   - Major findings: Critical compliance gaps, missing essential requirements, non-compliance with core standards
   - Minor findings: Less critical issues, partial compliance, areas needing clarification
   - Calculate the score based on the methodology above
   - Set "hasFindings": true
   - Set "message" to a brief summary of key issues

2. If NO findings exist (policy is fully compliant OR only minor rewording needed):
   - Set "findings" to an empty array: []
   - Set "score" with all requirements marked as compliant
   - Set "finalScore": 100
   - Set "hasFindings": false
   - Set "message": "No findings. Policy is fully compliant with the standard."

3. Always include:
   - Complete scoring breakdown
   - Final score (0-100)
   - Clear message indicating compliance status
   - "findingType" field for each finding (CRITICAL - must be "major" or "minor")

4. Order findings by:
   - First: findingType (major first, then minor)
   - Then: severity (critical first, then high, medium, low)

5. Be specific and precise - quote exact text from documents when possible

6. Ensure all revised text is clear, actionable, and directly addresses the standard requirement

7. DO NOT include findings that are:
   - Just rewording without substantive compliance value
   - Minor stylistic improvements
   - Text that already substantially meets requirements

IMPORTANT:
- Return ONLY valid JSON - no markdown, no explanations outside the JSON structure
- Use the retrieved document chunks to find exact text and references
- Be thorough but focused on actionable compliance gaps (major and minor only)
- Ensure revised text is professional and suitable for policy documents
- Always calculate and include the score, even if there are no findings
- CRITICAL: Every finding MUST have "findingType": "major" or "minor" - this is mandatory`;

  const [systemPrompt, setSystemPrompt] = useState<string>(defaultSystemPrompt);
  const storeIdRef = useRef<string | null>(null);
  const hasLoadedFilesRef = useRef<boolean>(false);

  useEffect(() => {
    storeIdRef.current = storeId;
  }, [storeId]);

  // Persist RAG service selection and update storeId
  useEffect(() => {
    if (user?.email) {
      localStorage.setItem(getRagServiceKey(), ragService);

      // When RAG service changes, try to load the storeId for that service
      const userEmail = user.email;
      const sanitizedEmail = userEmail.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
      const savedStoreId = localStorage.getItem(`quickCompare_storeId_${ragService}_${sanitizedEmail}`);

      if (savedStoreId) {
        setStoreId(savedStoreId);
      } else {
        // If no store ID for this service, reset it (will prompt to create/upload)
        setStoreId(null);
      }
    }
  }, [ragService, user?.email]);

  // Cleanup on unmount - don't delete store, keep it persistent
  // useEffect(() => {
  //   return () => {
  //     if (storeIdRef.current) {
  //       deleteRagStore(storeIdRef.current).catch((err) =>
  //         console.error('Error cleaning up RAG store:', err)
  //       );
  //     }
  //   };
  // }, []);

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return `File type ${file.type} is not supported. Please upload PDF, DOCX, TXT, or MD files.`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File size ${(file.size / 1024 / 1024).toFixed(2)}MB exceeds maximum of ${MAX_FILE_SIZE / 1024 / 1024}MB`;
    }
    return null;
  };

  const handleFileSelect = (
    file: File,
    type: 'policy' | 'standard'
  ): void => {
    const error = validateFile(file);
    if (error) {
      toast({
        title: 'Invalid File',
        description: error,
        variant: 'destructive',
      });
      return;
    }

    if (type === 'policy') {
      setPolicyFile(file);
    } else {
      setStandardFile(file);
    }
  };

  const handleDrop = useCallback(
    (
      e: React.DragEvent<HTMLDivElement>,
      type: 'policy' | 'standard'
    ): void => {
      e.preventDefault();
      e.stopPropagation();
      if (type === 'policy') {
        setIsDraggingPolicy(false);
      } else {
        setIsDraggingStandard(false);
      }

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFileSelect(files[0], type);
      }
    },
    []
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>, type: 'policy' | 'standard'): void => {
      e.preventDefault();
      e.stopPropagation();
      if (type === 'policy') {
        setIsDraggingPolicy(true);
      } else {
        setIsDraggingStandard(true);
      }
    },
    []
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent<HTMLDivElement>, type: 'policy' | 'standard'): void => {
      e.preventDefault();
      e.stopPropagation();
      if (type === 'policy') {
        setIsDraggingPolicy(false);
      } else {
        setIsDraggingStandard(false);
      }
    },
    []
  );

  const handleUploadAndProcess = async (): Promise<void> => {
    if (!policyFile && !standardFile && !selectedPolicyFile && selectedStandardFiles.length === 0) {
      toast({
        title: 'No Files Selected',
        description: 'Please upload or select at least one document (policy or standard)',
        variant: 'destructive',
      });
      return;
    }

    // Validate orgId is available before proceeding (for Knowledge Base RAG service)
    if (ragService !== 'gemini' && !orgId) {
      console.warn('⚠️ Organization ID not available, attempting to fetch...');
      // Try to fetch orgId synchronously
      try {
        if (user?.id) {
          const { data, error } = await offlineSupabase
            .rpc("get_user_organizations", { p_user_id: user.id });

          if (!error && data && data.length > 0) {
            setOrgId(data[0].id);
            console.log('✅ Successfully fetched orgId just-in-time:', data[0].id);
          } else {
            // Try fallback
            const orgIds = await enhancedClientService.getUserOrganizations();
            if (orgIds && orgIds.length > 0) {
              setOrgId(orgIds[0]);
              console.log('✅ Successfully fetched orgId via fallback:', orgIds[0]);
            } else {
              toast({
                title: 'Organization Required',
                description: 'Unable to determine your organization. Please refresh the page or contact support.',
                variant: 'destructive',
              });
              return;
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch orgId:', err);
        toast({
          title: 'Organization Required',
          description: 'Unable to determine your organization. Please refresh the page or contact support.',
          variant: 'destructive',
        });
        return;
      }
    }

    setStatus('processing');
    const totalSteps = (policyFile ? 1 : 0) + (standardFile ? 1 : 0) + 1; // files + create store (no generate questions)
    let currentStep = 0;

    try {
      // Always reuse the same store (single persistent store per user)
      let newStoreId = storeId || localStorage.getItem(getStoreIdKey());

      if (!newStoreId) {
        // Create new persistent store with user email
        setProgress({
          current: currentStep++,
          total: totalSteps,
          message: 'Creating document index...',
        });
        const storeName = getStoreName();
        newStoreId = await createRagStore(storeName, ragService, user?.id, orgId);
        setStoreId(newStoreId);
        localStorage.setItem(getStoreIdKey(), newStoreId);
      } else {
        setStoreId(newStoreId);
      }

      // Load existing files to refresh the list
      const currentFiles = await loadUploadedFiles(newStoreId);
      const loadedFiles = currentFiles || uploadedFiles;

      // Upload files (only if new files are selected, not if using existing files)
      if (policyFile) {
        // Check if file already exists in uploaded files list
        if (loadedFiles.some(f => f.name === policyFile.name)) {
          console.log('Policy file already exists in store, skipping upload:', policyFile.name);
          toast({
            title: 'File Already Uploaded',
            description: `${policyFile.name} is already in the store. Using existing file.`,
          });
        } else {
          setProgress({
            current: currentStep++,
            total: totalSteps,
            message: 'Uploading policy document...',
            fileName: policyFile.name,
          });
          try {
            await uploadToRagStore(newStoreId, policyFile, 'policy', ragService, user?.id, orgId);
          } catch (error) {
            // If store doesn't exist, create new one and retry
            if (error instanceof Error && (error.message === 'STORE_NOT_FOUND' || error.message.includes('STORE_NOT_FOUND'))) {
              console.warn('Store not found during upload, creating new persistent store');
              const storeName = getStoreName();
              const freshStoreId = await createRagStore(storeName, ragService, user?.id, orgId);
              localStorage.setItem(getStoreIdKey(), freshStoreId);
              setStoreId(freshStoreId);
              newStoreId = freshStoreId;
              // Retry upload
              await uploadToRagStore(freshStoreId, policyFile, 'policy', ragService, user?.id, orgId);
            } else {
              throw error;
            }
          }
        }
      }

      if (standardFile) {
        // Check if file already exists in uploaded files list
        if (loadedFiles.some(f => f.name === standardFile.name)) {
          console.log('Standard file already exists in store, skipping upload:', standardFile.name);
          toast({
            title: 'File Already Uploaded',
            description: `${standardFile.name} is already in the store. Using existing file.`,
          });
        } else {
          setProgress({
            current: currentStep++,
            total: totalSteps,
            message: 'Uploading standard document...',
            fileName: standardFile.name,
          });
          try {
            await uploadToRagStore(newStoreId, standardFile, 'standard', ragService, user?.id, orgId);
          } catch (error) {
            // If store doesn't exist, create new one and retry
            if (error instanceof Error && (error.message === 'STORE_NOT_FOUND' || error.message.includes('STORE_NOT_FOUND'))) {
              console.warn('Store not found during upload, creating new persistent store');
              const storeName = getStoreName();
              const freshStoreId = await createRagStore(storeName, ragService, user?.id, orgId);
              localStorage.setItem(getStoreIdKey(), freshStoreId);
              setStoreId(freshStoreId);
              newStoreId = freshStoreId;
              // Retry upload
              await uploadToRagStore(freshStoreId, standardFile, 'standard', ragService, user?.id, orgId);
            } else {
              throw error;
            }
          }
        }
      }

      // Note: If selectedPolicyFile or selectedStandardFile are set,
      // those files are already in the store, so no upload needed

      // Load list of uploaded files (refresh after uploads)
      const refreshedFiles = await loadUploadedFiles(newStoreId);

      // Ensure uploaded files state includes the files we just uploaded with correct categories
      // This is important so handleAutoRunAudit can identify which files are policy vs standard
      if (policyFile) {
        const policyFileEntry = refreshedFiles.find(f => f.name === policyFile.name);
        if (!policyFileEntry) {
          // Add to uploadedFiles if not already there
          setUploadedFiles(prev => {
            const exists = prev.some(f => f.name === policyFile.name);
            if (!exists) {
              return [...prev, { name: policyFile.name, category: 'policy' as const }];
            }
            return prev;
          });
        }
      }
      if (standardFile) {
        const standardFileEntry = refreshedFiles.find(f => f.name === standardFile.name);
        if (!standardFileEntry) {
          // Add to uploadedFiles if not already there
          setUploadedFiles(prev => {
            const exists = prev.some(f => f.name === standardFile.name);
            if (!exists) {
              return [...prev, { name: standardFile.name, category: 'standard' as const }];
            }
            return prev;
          });
        }
        // Auto-select the uploaded standard file
        setSelectedStandardFiles(prev => {
          if (!prev.includes(standardFile.name)) {
            return [...prev, standardFile.name];
          }
          return prev;
        });
      }

      setProgress({
        current: totalSteps,
        total: totalSteps,
        message: 'Upload complete! Running audit...',
      });

      // Update uploadedFiles state with refreshed files before auto-query
      setUploadedFiles(refreshedFiles);

      // Automatically run audit after upload
      await new Promise((resolve) => setTimeout(resolve, 500));
      setProgress(null);
      setStatus('chatting'); // Switch to chat view

      // Run automatic audit query
      await handleAutoRunAudit(newStoreId);
    } catch (error) {
      console.error('Error processing files:', error);
      toast({
        title: 'Processing Failed',
        description:
          error instanceof Error ? error.message : 'Failed to process documents',
        variant: 'destructive',
      });
      setStatus('upload');
      setProgress(null);
      if (storeId) {
        // Don't delete store on error - keep it persistent for retry
        // await deleteRagStore(storeId);
        setStoreId(null);
      }
    }
  };

  // Auto-run audit with system prompt only (first run)
  const handleAutoRunAudit = async (storeIdToUse: string): Promise<void> => {
    if (!storeIdToUse || isQueryLoading) return;

    // Validate orgId is available before proceeding (for Knowledge Base RAG service)
    if (ragService !== 'gemini' && !orgId) {
      console.warn('⚠️ Organization ID not available for audit, attempting to fetch...');
      try {
        if (user?.id) {
          const { data, error } = await offlineSupabase
            .rpc("get_user_organizations", { p_user_id: user.id });

          if (!error && data && data.length > 0) {
            setOrgId(data[0].id);
            console.log('✅ Successfully fetched orgId for audit:', data[0].id);
          } else {
            // Try fallback
            const orgIds = await enhancedClientService.getUserOrganizations();
            if (orgIds && orgIds.length > 0) {
              setOrgId(orgIds[0]);
              console.log('✅ Successfully fetched orgId via fallback for audit:', orgIds[0]);
            } else {
              toast({
                title: 'Organization Required',
                description: 'Unable to determine your organization. Please refresh the page.',
                variant: 'destructive',
              });
              return;
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch orgId for audit:', err);
        toast({
          title: 'Organization Required',
          description: 'Unable to determine your organization. Please refresh the page.',
          variant: 'destructive',
        });
        return;
      }
    }

    setIsQueryLoading(true);
    setOutput('');

    try {
      // Build enhanced system prompt with file category context
      let enhancedSystemPrompt = systemPrompt || '';

      // Get file categories from explicitly selected files only
      // Do NOT include files from uploadedFiles unless they were explicitly selected
      const policyFiles: string[] = [];
      const standardFiles: string[] = [];

      // Only use explicitly selected files
      if (selectedPolicyFile) {
        policyFiles.push(selectedPolicyFile);
        console.log('📋 Selected policy file:', selectedPolicyFile);
      }
      if (selectedStandardFiles.length > 0) {
        standardFiles.push(...selectedStandardFiles);
        console.log('📋 Selected standard files:', selectedStandardFiles);
      }

      // Also check uploaded files (for files just uploaded in this session)
      // Only add if they match the category and weren't already selected
      if (policyFile && policyFile.name && !policyFiles.includes(policyFile.name)) {
        policyFiles.push(policyFile.name);
        console.log('📤 Using uploaded policy file:', policyFile.name);
      }
      if (standardFile && standardFile.name && !standardFiles.includes(standardFile.name)) {
        standardFiles.push(standardFile.name);
        console.log('📤 Using uploaded standard file:', standardFile.name);
      }

      // Add file context to system prompt
      if (policyFiles.length > 0 || standardFiles.length > 0) {
        const fileContext = [
          'IMPORTANT CONTEXT:',
          policyFiles.length > 0
            ? `Policy Document(s): ${policyFiles.join(', ')}`
            : 'No policy documents specified.',
          standardFiles.length > 0
            ? `Standard Document(s): ${standardFiles.join(', ')}`
            : 'No standard documents specified.',
          '',
          'When analyzing compliance, compare the POLICY document(s) against the STANDARD document(s).',
          'The policy document is what needs to be audited for compliance.',
          'The standard document contains the requirements that the policy must comply with.',
        ].join('\n');

        enhancedSystemPrompt = `${fileContext}\n\n${enhancedSystemPrompt}`;
      }
      // Run with empty query - only enhanced system prompt will be used
      // Pass userId and orgId for Supabase RLS filtering
      let resultText = '';

      // Use multi-agent comparison when we have a policy and standard(s)
      // The multi-agent service provides value through:
      // 1. Intelligent section extraction from policy
      // 2. Structured analysis of each section
      // 3. Comprehensive synthesis report
      // This is valuable even with a single standard
      if (policyFiles.length > 0 && standardFiles.length >= 1) {
        console.log('🚀 Using multi-agent comparison service', {
          policy: policyFiles[0],
          standards: standardFiles,
          standardCount: standardFiles.length,
          userId: user?.id,
          orgId
        });

        // Initialize streaming state
        setIsStreaming(true);
        setInterimFindings([]);
        setStreamingProgress({ step: 'init', message: 'Initializing multi-agent comparison...' });

        // Use streaming service
        await compareMultipleStandardsStream(
          policyFiles[0],
          standardFiles,
          (event) => {
            if (event.type === 'step_start') {
              setStreamingProgress({ step: event.step, message: event.message });
            } else if (event.type === 'interim_finding' || event.type === 'holistic_finding') {
              console.log('📊 Received finding:', event.finding);
              // Append finding only if it doesn't already exist (deduplicate by sectionId + standardId + reference)
              setInterimFindings(prev => {
                const findingId = `${event.finding.sectionId || event.finding.statementId || 'unknown'}-${event.finding.standardId}-${event.finding.reference || 'no-ref'}`;
                const exists = prev.some(f => {
                  const existingId = `${f.sectionId || f.statementId || 'unknown'}-${f.standardId}-${f.reference || 'no-ref'}`;
                  return existingId === findingId;
                });
                if (exists) {
                  return prev; // Don't add duplicate
                }
                return [...prev, event.finding]; // Append new finding
              });
            } else if (event.type === 'final_report') {
              resultText = event.report;
              // Don't set output yet, let the function finish
            } else if (event.type === 'error') {
              console.error('Streaming error:', event.message);
              toast({
                title: "Streaming Warning",
                description: event.message,
                variant: "destructive"
              });
            }
          },
          enhancedSystemPrompt,
          user?.id,
          orgId
        );

        setIsStreaming(false);
        // resultText is already set by the event handler if successful
        if (!resultText) resultText = 'No response received';

        console.log('✅ Multi-agent comparison complete', {
          reportLength: resultText.length,
          findingsCount: interimFindings.length,
          sectionsAnalyzed: streamingProgress.step === 'synthesizeFinal' ? 'complete' : 'partial'
        });
      } else {
        // Fallback to standard RAG query if no clear policy/standard structure
        console.log('📄 Using standard RAG query (no policy/standard structure)', {
          storeId: storeIdToUse,
          ragService,
          userId: user?.id,
          orgId
        });

        const result = await queryDocuments(storeIdToUse, '', enhancedSystemPrompt, ragService, user?.id, orgId);
        resultText = result.text || 'No response received';
      }

      setOutput(resultText);
      // Persist output and status to localStorage
      if (user?.email) {
        localStorage.setItem(getOutputKey(), resultText);
        localStorage.setItem(getStatusKey(), 'chatting');
      }
    } catch (error) {
      console.error('Error running audit:', error);
      const errorText = `Error: ${error instanceof Error ? error.message : 'Failed to process audit'}`;
      setOutput(errorText);
      // Persist error message to localStorage
      if (user?.email) {
        localStorage.setItem(getOutputKey(), errorText);
        localStorage.setItem(getStatusKey(), 'chatting');
      }
      toast({
        title: 'Audit Failed',
        description:
          error instanceof Error ? error.message : 'Failed to process audit',
        variant: 'destructive',
      });
    } finally {
      setIsQueryLoading(false);
    }
  };

  // Manual query submission (for subsequent queries after initial audit)
  const handleSubmitQuery = async (): Promise<void> => {
    if (!storeId || isQueryLoading) return;

    if (!query.trim()) {
      toast({
        title: 'Query Required',
        description: 'Please enter a query',
        variant: 'destructive',
      });
      return;
    }

    setIsQueryLoading(true);
    setOutput('');

    try {
      // Pass userId and orgId for Supabase RLS filtering
      const result = await queryDocuments(storeId, query, systemPrompt, ragService, user?.id, orgId);
      const resultText = result.text || 'No response received';
      setOutput(resultText);
      // Persist output to localStorage
      if (user?.email) {
        localStorage.setItem(getOutputKey(), resultText);
        localStorage.setItem(getStatusKey(), 'chatting');
      }
    } catch (error) {
      console.error('Error querying documents:', error);
      setOutput(
        `Error: ${error instanceof Error ? error.message : 'Failed to process query'}`
      );
      toast({
        title: 'Query Failed',
        description:
          error instanceof Error ? error.message : 'Failed to process query',
        variant: 'destructive',
      });
    } finally {
      setIsQueryLoading(false);
    }
  };


  const loadUploadedFiles = async (storeIdToLoad: string): Promise<Array<{ name: string; category: 'policy' | 'standard' }>> => {
    try {
      setIsLoadingFiles(true);
      // Load all files (no category filter)
      console.log('Fetching files with:', { storeId: storeIdToLoad, ragService, userId: user?.id, orgId });
      const files = await listFilesInStore(storeIdToLoad, undefined, ragService, user?.id, orgId);
      console.log('Fetched files:', files);
      setUploadedFiles(files);
      hasLoadedFilesRef.current = true; // Mark as loaded
      console.log('Loaded uploaded files', { storeId: storeIdToLoad, fileCount: files.length });
      return files;
    } catch (error) {
      // Silently handle errors (server might not be running)
      // Don't clear existing files on error - preserve what we have
      console.warn('Could not load uploaded files (server may not be running):', error);
      // Return empty array but don't clear state - let user retry manually
      return [];
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const handleSelectExistingFile = (
    fileName: string,
    fileType: 'policy' | 'standard'
  ): void => {
    // Select existing file from store (no upload needed)
    if (fileType === 'policy') {
      setSelectedPolicyFile(fileName);
      setPolicyFile(null); // Clear uploaded file if any
    } else {
      // Toggle selection for standards
      setSelectedStandardFiles(prev => {
        if (prev.includes(fileName)) {
          return prev.filter(f => f !== fileName);
        } else {
          return [...prev, fileName];
        }
      });
      setStandardFile(null); // Clear uploaded file if any
    }

    // Ensure the selected file is in uploadedFiles with correct category
    // This ensures handleAutoRunAudit can identify file categories
    setUploadedFiles(prev => {
      const exists = prev.some(f => f.name === fileName);
      if (!exists) {
        return [...prev, { name: fileName, category: fileType }];
      } else {
        // Update category if it's different
        return prev.map(f =>
          f.name === fileName ? { ...f, category: fileType } : f
        );
      }
    });

    toast({
      title: 'File Selected',
      description: `Using existing file: ${fileName}. No upload needed.`,
    });
  };

  // Parse and format audit results JSON
  const renderFormattedAuditResults = (outputText: string): React.ReactNode => {
    if (!outputText) return null;

    let jsonText = outputText.trim();
    let parsedData: {
      findings?: Array<{
        originalText?: string;
        revisedText?: string;
        reference?: string;
        complianceStatus?: string;
        severity?: string;
        findingType?: 'major' | 'minor';
        explanation?: string;
        analysis?: string;
        gaps?: string[];
        recommendations?: string[];
      }>;
      score?: {
        totalRequirements?: number;
        compliantRequirements?: number;
        nonCompliantRequirements?: number;
        missingRequirements?: number;
        needsClarification?: number;
        scoreBreakdown?: {
          critical?: number;
          high?: number;
          medium?: number;
          low?: number;
        };
        finalScore?: number;
        scoreExplanation?: string;
      };
      hasFindings?: boolean;
      message?: string;
    } | null = null;

    let auditResults: Array<{
      originalText?: string;
      revisedText?: string;
      reference?: string;
      complianceStatus?: string;
      severity?: string;
      findingType?: 'major' | 'minor';
      explanation?: string;
      analysis?: string;
      gaps?: string[];
      recommendations?: string[];
    }> | null = null;

    try {
      // Step 1: Try to extract JSON from markdown code blocks
      const codeBlockRegex = /```(?:json)?\s*([\s\S]*?)\s*```/g;
      const codeBlockMatches = [...jsonText.matchAll(codeBlockRegex)];
      if (codeBlockMatches.length > 0) {
        jsonText = codeBlockMatches[codeBlockMatches.length - 1][1].trim();
      }

      // Step 2: Try to find JSON array/object
      const jsonArrayMatch = jsonText.match(/\[[\s\S]*\]/);
      const jsonObjectMatch = jsonText.match(/\{[\s\S]*\}/);

      if (jsonObjectMatch) {
        // New format: JSON object with findings, score, etc.
        try {
          parsedData = JSON.parse(jsonObjectMatch[0]);
          if (parsedData.findings && Array.isArray(parsedData.findings)) {
            auditResults = parsedData.findings;
          }
        } catch {
          // Try old format
          if (jsonArrayMatch) {
            jsonText = jsonArrayMatch[0];
          }
        }
      } else if (jsonArrayMatch) {
        // Old format: JSON array (backward compatibility)
        jsonText = jsonArrayMatch[0];
      }

      // Step 3: Clean up formatting
      jsonText = jsonText
        .replace(/^[^{[]*/, '')
        .replace(/[^}\]]*$/, '')
        .replace(/```json\s*/g, '')
        .replace(/```\s*/g, '')
        .trim();

      // Step 4: Parse JSON
      if (!parsedData) {
        const parsed = JSON.parse(jsonText);
        if (Array.isArray(parsed)) {
          // Old format: array of findings
          auditResults = parsed;
          parsedData = { findings: parsed, hasFindings: parsed.length > 0 };
        } else if (parsed.findings && Array.isArray(parsed.findings)) {
          // New format: object with findings
          parsedData = parsed;
          auditResults = parsed.findings;
        } else {
          // Single finding object
          auditResults = [parsed];
          parsedData = { findings: [parsed], hasFindings: true };
        }
      }
    } catch (e) {
      // JSON parsing failed
      auditResults = null;
      parsedData = null;
    }

    // If we successfully parsed JSON findings, render them
    if (parsedData && auditResults !== null) {
      const getSeverityColor = (severity?: string): string => {
        switch (severity?.toLowerCase()) {
          case 'critical':
            return 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20';
          case 'high':
            return 'bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/20';
          case 'medium':
            return 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20';
          case 'low':
            return 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20';
          default:
            return 'bg-muted text-muted-foreground';
        }
      };

      const getStatusColor = (status?: string): string => {
        switch (status?.toLowerCase()) {
          case 'non-compliant':
            return 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20';
          case 'missing':
            return 'bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/20';
          case 'needs-clarification':
            return 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20';
          default:
            return 'bg-muted text-muted-foreground';
        }
      };

      const getFindingTypeColor = (type?: string): string => {
        switch (type?.toLowerCase()) {
          case 'major':
            return 'bg-red-600/20 text-red-800 dark:text-red-300 border-red-600/30 font-semibold';
          case 'minor':
            return 'bg-blue-600/20 text-blue-800 dark:text-blue-300 border-blue-600/30 font-semibold';
          default:
            return 'bg-muted text-muted-foreground';
        }
      };

      return (
        <div className="space-y-6">
          {/* Score Summary Card */}
          {parsedData.score && (
            <Card className="border-primary/20 bg-primary/5">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Compliance Score</span>
                  <Badge className="text-2xl px-4 py-2" variant={parsedData.score.finalScore && parsedData.score.finalScore >= 80 ? 'default' : parsedData.score.finalScore && parsedData.score.finalScore >= 60 ? 'secondary' : 'destructive'}>
                    {parsedData.score.finalScore ?? 0}/100
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {parsedData.score.scoreBreakdown && (
                  <div className="grid grid-cols-4 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Critical:</span> <strong className="text-red-600">{parsedData.score.scoreBreakdown.critical ?? 0}</strong>
                    </div>
                    <div>
                      <span className="text-muted-foreground">High:</span> <strong className="text-orange-600">{parsedData.score.scoreBreakdown.high ?? 0}</strong>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Medium:</span> <strong className="text-yellow-600">{parsedData.score.scoreBreakdown.medium ?? 0}</strong>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Low:</span> <strong className="text-blue-600">{parsedData.score.scoreBreakdown.low ?? 0}</strong>
                    </div>
                  </div>
                )}
                {parsedData.score.scoreExplanation && (
                  <p className="text-sm text-muted-foreground">{parsedData.score.scoreExplanation}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Message Banner */}
          {parsedData.message && (
            <Alert variant={parsedData.hasFindings ? 'default' : 'default'} className={parsedData.hasFindings ? 'border-yellow-500/20 bg-yellow-500/5' : 'border-green-500/20 bg-green-500/5'}>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{parsedData.message}</AlertDescription>
            </Alert>
          )}

          {/* No Findings Case */}
          {(!parsedData.hasFindings || (auditResults && auditResults.length === 0)) && (
            <Card className="border-green-500/20 bg-green-500/5">
              <CardContent className="py-8 text-center">
                <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Compliance Issues Found</h3>
                <p className="text-muted-foreground">
                  {parsedData.message || 'Policy is fully compliant with the standard.'}
                </p>
                {parsedData.score && (
                  <div className="mt-4">
                    <Badge className="text-xl px-4 py-2" variant="default">
                      Score: {parsedData.score.finalScore ?? 100}/100
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Findings List */}
          {auditResults && auditResults.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Found <strong>{auditResults.length}</strong> compliance {auditResults.length === 1 ? 'finding' : 'findings'}
                </p>
              </div>

              <div className="space-y-4">
                {auditResults.map((finding, index) => (
                  <Card key={index} className="border-l-4 border-l-primary">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle className="text-lg">
                          Finding #{index + 1}
                        </CardTitle>
                        <div className="flex gap-2 flex-wrap">
                          {finding.findingType && (
                            <Badge className={getFindingTypeColor(finding.findingType)}>
                              {finding.findingType.toUpperCase()}
                            </Badge>
                          )}
                          {finding.severity && (
                            <Badge className={getSeverityColor(finding.severity)}>
                              {finding.severity.toUpperCase()}
                            </Badge>
                          )}
                          {finding.complianceStatus && (
                            <Badge className={getStatusColor(finding.complianceStatus)}>
                              {finding.complianceStatus.replace('-', ' ').toUpperCase()}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {finding.explanation && (
                        <div>
                          <Label className="text-sm font-semibold mb-2 block">Explanation</Label>
                          <p className="text-sm text-muted-foreground">{finding.explanation}</p>
                        </div>
                      )}

                      {/* originalText - Always show if present or if finding exists */}
                      <div>
                        <Label className="text-sm font-semibold mb-2 block text-destructive">
                          Original Text {finding.originalText ? '(Non-Compliant)' : '(Missing Content)'}
                        </Label>
                        <div className="bg-destructive/10 p-3 rounded-md text-sm border border-destructive/20">
                          {finding.originalText ? `"${finding.originalText}"` : '(No existing text - content needs to be added)'}
                        </div>
                      </div>

                      {finding.revisedText && (
                        <div>
                          <Label className="text-sm font-semibold mb-2 block text-green-600 dark:text-green-400">
                            Recommended Revision
                          </Label>
                          <div className="bg-green-500/10 p-3 rounded-md text-sm border border-green-500/20">
                            "{finding.revisedText}"
                          </div>
                        </div>
                      )}

                      {finding.reference && (
                        <div>
                          <Label className="text-sm font-semibold mb-2 block">Reference</Label>
                          <p className="text-sm text-muted-foreground">{finding.reference}</p>
                        </div>
                      )}

                      {finding.analysis && (
                        <div>
                          <Label className="text-sm font-semibold mb-2 block">Analysis</Label>
                          <p className="text-sm text-muted-foreground">{finding.analysis}</p>
                        </div>
                      )}

                      {/* gaps - Always show */}
                      <div>
                        <Label className="text-sm font-semibold mb-2 block">Gaps</Label>
                        {finding.gaps && finding.gaps.length > 0 ? (
                          <ul className="list-disc list-inside text-sm text-red-600 dark:text-red-400 space-y-1">
                            {finding.gaps.map((gap, i) => (
                              <li key={i}>{gap}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-muted-foreground italic">No specific gaps identified</p>
                        )}
                      </div>

                      {/* recommendations - Always show */}
                      <div>
                        <Label className="text-sm font-semibold mb-2 block">Recommendations</Label>
                        {finding.recommendations && finding.recommendations.length > 0 ? (
                          <ul className="list-disc list-inside text-sm text-green-600 dark:text-green-400 space-y-1">
                            {finding.recommendations.map((rec, i) => (
                              <li key={i}>{rec}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-muted-foreground italic">No specific recommendations provided</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    // Fallback: Render as Markdown Report (for multi-agent comparison)
    return (
      <Card>
        <CardHeader>
          <CardTitle>Compliance Report</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="prose dark:prose-invert max-w-none font-sans">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {outputText}
            </ReactMarkdown>
          </div>
        </CardContent>
      </Card>
    );
  };

  const handleNewChat = async (): Promise<void> => {
    try {
      // Just clear selections and reset - don't delete the store
      // Keep the store so files persist
      const savedStoreId = localStorage.getItem(getStoreIdKey()) || storeId;
      if (savedStoreId) {
        setStoreId(savedStoreId);
        // Reload files
        try {
          await loadUploadedFiles(savedStoreId);
        } catch (error) {
          console.warn('Error reloading files for new audit:', error);
          // Continue anyway - files might not be critical
        }
      }
      setPolicyFile(null);
      setStandardFile(null);
      setSelectedPolicyFile(null);
      setSelectedStandardFiles([]);
      setQuery('');
      setOutput('');
      setIsStreaming(false);
      setInterimFindings([]);
      setStatus('upload'); // Go back to upload view
      // Clear persisted output and status
      if (user?.email) {
        localStorage.removeItem(getOutputKey());
        localStorage.removeItem(getStatusKey());
      }

      toast({
        title: 'New Audit Started',
        description: 'You can now upload new files or select existing ones.',
      });
    } catch (error) {
      console.error('Error in handleNewChat:', error);
      toast({
        title: 'Error',
        description: 'Failed to start new audit. Please try again.',
        variant: 'destructive',
      });
    }
    // Reset to default system prompt (same as initial state)
    setSystemPrompt(defaultSystemPrompt);
    setStatus('upload');
  };

  // Auto-scroll interim findings to bottom when new findings are added
  useEffect(() => {
    if (interimFindingsScrollRef.current && interimFindings.length > 0) {
      interimFindingsScrollRef.current.scrollTop = interimFindingsScrollRef.current.scrollHeight;
    }
  }, [interimFindings]);

  // On mount, check for existing store and load files - use single persistent store
  useEffect(() => {
    const checkExistingStore = async (): Promise<void> => {
      // Wait for user to be available before checking store
      if (!user) {
        setIsCheckingStore(false);
        return;
      }

      // Wait for orgId to be available (needed for Supabase RLS filtering)
      // If orgId is not available yet, we'll skip loading files for now
      // The user can manually refresh or upload files later
      if (!orgId) {
        setIsCheckingStore(false);
        return;
      }

      // Prevent re-running if we've already loaded files for this store
      // Reset the flag if storeId changed
      let savedStoreId = localStorage.getItem(getStoreIdKey());
      if (savedStoreId !== storeIdRef.current) {
        hasLoadedFilesRef.current = false; // Reset flag if store changed
      }
      if (hasLoadedFilesRef.current && savedStoreId === storeIdRef.current) {
        return; // Already loaded files for this store
      }

      setIsCheckingStore(true);

      // First, check if we have saved output/status to restore
      // If we do, restore them immediately to prevent interruption
      const savedOutput = localStorage.getItem(getOutputKey());
      const savedStatus = localStorage.getItem(getStatusKey());

      // Clear any stuck 'processing' status
      if (savedStatus === 'processing') {
        localStorage.removeItem(getStatusKey());
        localStorage.removeItem(getOutputKey());
      }

      // Restore output if available, but don't automatically switch to chatting view
      // This allows users to see the upload view even when previous results exist
      // They can view results by clicking "View Results" or running a new audit
      if (savedOutput) {
        setOutput(savedOutput);
        // Don't automatically set status to 'chatting' - let user choose to view results
        // Status will be set to 'upload' when files are loaded, allowing upload of new files
      }

      // Use savedStoreId that was already retrieved above

      if (!savedStoreId) {
        // Create a new store if none exists (with user email)
        try {
          const storeName = getStoreName();
          savedStoreId = await createRagStore(storeName);
          localStorage.setItem(getStoreIdKey(), savedStoreId);
          setStoreId(savedStoreId);
          setUploadedFiles([]); // New store, so no files yet
          hasLoadedFilesRef.current = true; // Mark as checked (even though empty)
          setStatus('upload');
        } catch (error) {
          // Server might not be running - mark as checked to prevent retries
          const errorMessage = error instanceof Error ? error.message : String(error);
          const isServerError = errorMessage.includes('Network Error') ||
            errorMessage.includes('ERR_CONNECTION_REFUSED') ||
            errorMessage.includes('ERR_NETWORK');

          if (isServerError) {
            console.warn('Server not available - cannot create store. Please start the server and refresh.', error);
            // Mark as checked so we don't keep retrying
            hasLoadedFilesRef.current = true;
            setStatus('upload');
          } else {
            // Other errors - mark as checked and show upload view
            console.warn('Could not create store:', error);
            hasLoadedFilesRef.current = true;
            setStatus('upload');
          }
        }
      } else {
        // Load existing store and files
        try {
          setStoreId(savedStoreId);
          const files = await listFilesInStore(savedStoreId, undefined, ragService, user?.id, orgId);
          setUploadedFiles(files);
          hasLoadedFilesRef.current = true; // Mark as loaded

          // Always show upload view when files are loaded, even if there's saved output
          // This allows users to upload more files or start a new audit
          // They can still view previous results by clicking on them or running a new query
          setStatus('upload');
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);

          // Check if store doesn't exist
          if (errorMessage === 'STORE_NOT_FOUND') {
            // Store doesn't exist - clear it and create a new one
            console.warn('Store does not exist, creating new persistent store');
            localStorage.removeItem(getStoreIdKey());
            try {
              const storeName = getStoreName();
              const newStoreId = await createRagStore(storeName);
              localStorage.setItem(getStoreIdKey(), newStoreId);
              setStoreId(newStoreId);
              setUploadedFiles([]);
              setStatus('upload');
            } catch (createError) {
              console.error('Error creating new store:', createError);
              setStatus('upload');
            }
          } else if (errorMessage === 'SERVER_NOT_AVAILABLE' ||
            errorMessage.includes('Network Error') ||
            errorMessage.includes('ERR_CONNECTION_REFUSED') ||
            errorMessage.includes('ERR_NETWORK')) {
            // Server not running - don't clear files, just mark as checked
            console.warn('Server not available - cannot load files. Please start the server and refresh.');
            hasLoadedFilesRef.current = true; // Mark as checked to prevent retries
            setStatus('upload');
            // Don't clear storeId or files - they might be valid, just server is down
          } else {
            // Other errors - clear stale store ID to force recreation
            console.warn('Error loading store, clearing stale ID:', errorMessage);
            localStorage.removeItem(getStoreIdKey());
            setStoreId(null);
            setUploadedFiles([]);
            hasLoadedFilesRef.current = false; // Reset flag for retry
            setStatus('upload');
          }
        }
      }
      setIsCheckingStore(false);
    };

    checkExistingStore();
  }, [user, orgId]); // Re-run when user or orgId loads

  // Load files when storeId changes
  useEffect(() => {
    if (storeId && uploadedFiles.length === 0 && orgId) {
      loadUploadedFiles(storeId);
    }
  }, [storeId, orgId]);

  const renderUploadSection = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="text-2xl md:text-3xl font-bold mb-2">Policy Compliance Auditor</h1>
        <p className="text-muted-foreground">
          Upload your policy document and the standards document it should comply with. The AI will assess compliance and provide specific recommendations with references.
        </p>
        {output && (
          <div className="mt-4">
            <Button
              variant="outline"
              onClick={() => setStatus('chatting')}
              className="mt-2"
            >
              <FileText className="h-4 w-4 mr-2" />
              View Previous Results
            </Button>
          </div>
        )}
      </div>

      {/* Existing Files Section */}
      {storeId && uploadedFiles.length > 0 && (
        <Card className="border-primary/20">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Select Existing Files ({uploadedFiles.filter(f => f.category === 'policy').length} policy, {uploadedFiles.filter(f => f.category === 'standard').length} standard)
              </CardTitle>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteStore}
              >
                <X className="h-4 w-4 mr-2" />
                Delete Store
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Policy File Selection */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Policy Document:</Label>
                <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto">
                  {uploadedFiles
                    .filter(file => file.category === 'policy')
                    .map((file) => (
                      <Button
                        key={`policy-${file.name}`}
                        variant={selectedPolicyFile === file.name ? 'default' : 'outline'}
                        size="sm"
                        className="justify-start text-left"
                        onClick={() => handleSelectExistingFile(file.name, 'policy')}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        <span className="truncate">{file.name}</span>
                      </Button>
                    ))}
                  {uploadedFiles.filter(file => file.category === 'policy').length === 0 && (
                    <p className="text-xs text-muted-foreground py-2">No policy documents uploaded yet</p>
                  )}
                </div>
                {selectedPolicyFile && (
                  <p className="text-xs text-muted-foreground">
                    Selected: <strong>{selectedPolicyFile}</strong>
                  </p>
                )}
              </div>

              {/* Standard File Selection */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Standard Document:</Label>
                <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto">
                  {uploadedFiles
                    .filter(file => file.category === 'standard')
                    .map((file) => (
                      <Button
                        key={`standard-${file.name}`}
                        variant={selectedStandardFiles.includes(file.name) ? 'default' : 'outline'}
                        size="sm"
                        className="justify-start text-left"
                        onClick={() => handleSelectExistingFile(file.name, 'standard')}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        <span className="truncate">{file.name}</span>
                      </Button>
                    ))}
                  {uploadedFiles.filter(file => file.category === 'standard').length === 0 && (
                    <p className="text-xs text-muted-foreground py-2">No standard documents uploaded yet</p>
                  )}
                </div>
                {selectedStandardFiles.length > 0 && (
                  <div className="text-sm text-muted-foreground mt-2">
                    Selected: <strong>{selectedStandardFiles.join(', ')}</strong>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Divider */}
      {storeId && uploadedFiles.length > 0 && (
        <div className="flex items-center gap-4">
          <div className="flex-1 border-t"></div>
          <span className="text-sm text-muted-foreground">OR</span>
          <div className="flex-1 border-t"></div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Policy Upload */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Policy Document
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Existing Files Selection */}
            {uploadedFiles.filter(file => file.category === 'policy').length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Or Select Existing File:</Label>
                <div className="grid grid-cols-1 gap-2 max-h-32 overflow-y-auto">
                  {uploadedFiles
                    .filter(file => file.category === 'policy')
                    .map((file) => (
                      <Button
                        key={`policy-${file.name}`}
                        variant={selectedPolicyFile === file.name ? 'default' : 'outline'}
                        size="sm"
                        className="justify-start text-left"
                        onClick={() => handleSelectExistingFile(file.name, 'policy')}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        <span className="truncate">{file.name}</span>
                      </Button>
                    ))}
                </div>
              </div>
            )}

            <div className="text-center text-sm text-muted-foreground">OR</div>

            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${isDraggingPolicy
                ? 'border-primary bg-primary/10'
                : 'border-muted-foreground/50'
                }`}
              onDrop={(e) => handleDrop(e, 'policy')}
              onDragOver={(e) => handleDragOver(e, 'policy')}
              onDragLeave={(e) => handleDragLeave(e, 'policy')}
            >
              {policyFile ? (
                <div className="space-y-2">
                  <p className="font-medium">{policyFile.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(policyFile.size / 1024).toFixed(2)} KB
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPolicyFile(null);
                      setSelectedPolicyFile(null);
                    }}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Remove
                  </Button>
                </div>
              ) : selectedPolicyFile ? (
                <div className="space-y-2">
                  <p className="font-medium">{selectedPolicyFile}</p>
                  <p className="text-sm text-muted-foreground">Existing file in store</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedPolicyFile(null);
                      setPolicyFile(null);
                    }}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Remove
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <Upload className="h-12 w-12 mx-auto text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium mb-1">
                      Drag & drop or click to upload
                    </p>
                    <p className="text-xs text-muted-foreground">
                      PDF, DOCX, TXT, MD
                    </p>
                  </div>
                  <Input
                    type="file"
                    accept=".pdf,.docx,.doc,.txt,.md"
                    className="hidden"
                    id="policy-upload"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileSelect(file, 'policy');
                    }}
                  />
                  <Button
                    variant="outline"
                    onClick={() =>
                      document.getElementById('policy-upload')?.click()
                    }
                  >
                    Select File
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Standard Upload */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GitCompare className="h-5 w-5" />
              Standard Document
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Existing Files Selection */}
            {uploadedFiles.filter(file => file.category === 'standard').length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Or Select Existing File:</Label>
                <div className="grid grid-cols-1 gap-2 max-h-32 overflow-y-auto">
                  {uploadedFiles
                    .filter(file => file.category === 'standard')
                    .map((file) => (
                      <Button
                        key={`standard-${file.name}`}
                        variant={selectedStandardFiles.includes(file.name) ? 'default' : 'outline'}
                        size="sm"
                        className="justify-start text-left"
                        onClick={() => handleSelectExistingFile(file.name, 'standard')}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        <span className="truncate">{file.name}</span>
                      </Button>
                    ))}
                </div>
              </div>
            )}

            <div className="text-center text-sm text-muted-foreground">OR</div>

            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${isDraggingStandard
                ? 'border-primary bg-primary/10'
                : 'border-muted-foreground/50'
                }`}
              onDrop={(e) => handleDrop(e, 'standard')}
              onDragOver={(e) => handleDragOver(e, 'standard')}
              onDragLeave={(e) => handleDragLeave(e, 'standard')}
            >
              {standardFile ? (
                <div className="space-y-2">
                  <p className="font-medium">{standardFile.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(standardFile.size / 1024).toFixed(2)} KB
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setStandardFile(null);
                      // Don't clear selectedStandardFiles here as this is for the uploaded file
                    }}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Remove
                  </Button>
                </div>
              ) : selectedStandardFiles.length > 0 ? (
                <div className="space-y-2">
                  <p className="font-medium">{selectedStandardFiles.length} file(s) selected</p>
                  <div className="text-sm text-muted-foreground">
                    {selectedStandardFiles.slice(0, 3).join(', ')}
                    {selectedStandardFiles.length > 3 && ` +${selectedStandardFiles.length - 3} more`}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedStandardFiles([]);
                    }}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Clear Selection
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <Upload className="h-12 w-12 mx-auto text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium mb-1">
                      Drag & drop or click to upload
                    </p>
                    <p className="text-xs text-muted-foreground">
                      PDF, DOCX, TXT, MD
                    </p>
                  </div>
                  <Input
                    type="file"
                    accept=".pdf,.docx,.doc,.txt,.md"
                    className="hidden"
                    id="standard-upload"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileSelect(file, 'standard');
                    }}
                  />
                  <Button
                    variant="outline"
                    onClick={() =>
                      document.getElementById('standard-upload')?.click()
                    }
                  >
                    Select File
                  </Button>
                </div>
              )}

            </div>
          </CardContent>
        </Card >
      </div >

      {/* System Prompt Configuration */}
      < Card >
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            System Prompt Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="system-prompt">
              System Prompt (Policy Auditor Instructions)
            </Label>
            <Textarea
              id="system-prompt"
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              placeholder="Enter system prompt instructions..."
              className="min-h-[200px] font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              This prompt configures the AI as a policy auditor. The default prompt instructs the AI to return JSON format with originalText, revisedText, and reference fields for each compliance finding. Edit to customize the auditing behavior.
            </p>
          </div>
        </CardContent>
      </Card >

      <div className="flex justify-center">
        <Button
          onClick={handleUploadAndProcess}
          disabled={!policyFile && !standardFile && !selectedPolicyFile && selectedStandardFiles.length === 0}
          size="lg"
        >
          <GitCompare className="h-5 w-5 mr-2" />
          Start Compliance Audit
        </Button>
      </div>
    </div >
  );

  const renderProcessingSection = () => (
    <div className="flex flex-col items-center justify-center space-y-6 py-12">
      <Loader2 className="h-12 w-12 animate-spin text-primary" />
      {progress && (
        <div className="w-full max-w-md space-y-2">
          <p className="text-center font-medium">{progress.message}</p>
          {progress.fileName && (
            <p className="text-center text-sm text-muted-foreground">
              {progress.fileName}
            </p>
          )}
          <Progress
            value={(progress.current / progress.total) * 100}
            className="w-full"
          />
        </div>
      )}
    </div>
  );

  const renderFileSelectionSection = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="text-3xl font-bold mb-2">Select Files to Query</h1>
        <p className="text-muted-foreground">
          Choose files from your store to query, or upload new files.
        </p>
      </div>

      {isLoadingFiles ? (
        <Card>
          <CardContent className="py-8">
            <div className="flex items-center justify-center space-x-2">
              <Loader2 className="h-6 w-6 animate-spin" />
              <p className="text-muted-foreground">Loading files...</p>
            </div>
          </CardContent>
        </Card>
      ) : uploadedFiles.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground mb-4">
              No files found in store. Please upload files first.
            </p>
            <Button onClick={() => setStatus('upload')}>
              <Upload className="h-4 w-4 mr-2" />
              Upload Files
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Policy File Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Policy Document
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Label className="text-sm font-medium">Select Policy File:</Label>
                <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto">
                  {uploadedFiles
                    .filter(file => file.category === 'policy')
                    .map((file) => (
                      <Button
                        key={`policy-${file.name}`}
                        variant={selectedPolicyFile === file.name ? 'default' : 'outline'}
                        size="sm"
                        className="justify-start text-left"
                        onClick={() => handleSelectExistingFile(file.name, 'policy')}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        <span className="truncate">{file.name}</span>
                      </Button>
                    ))}
                  {uploadedFiles.filter(file => file.category === 'policy').length === 0 && (
                    <p className="text-xs text-muted-foreground py-2">No policy documents uploaded yet</p>
                  )}
                </div>
                {selectedPolicyFile && (
                  <div className="mt-2 p-2 bg-muted rounded text-sm">
                    Selected: <strong>{selectedPolicyFile}</strong>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Standard File Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GitCompare className="h-5 w-5" />
                  Standard Document
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Label className="text-sm font-medium">Select Standard File:</Label>
                <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto">
                  {uploadedFiles
                    .filter(file => file.category === 'standard')
                    .map((file) => (
                      <Button
                        key={`standard-${file.name}`}
                        variant={selectedStandardFiles.includes(file.name) ? 'default' : 'outline'}
                        size="sm"
                        className="justify-start text-left"
                        onClick={() => handleSelectExistingFile(file.name, 'standard')}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        <span className="truncate">{file.name}</span>
                      </Button>
                    ))}
                  {uploadedFiles.filter(file => file.category === 'standard').length === 0 && (
                    <p className="text-xs text-muted-foreground py-2">No standard documents uploaded yet</p>
                  )}
                </div>
                {selectedStandardFiles.length > 0 && (
                  <div className="mt-2 p-2 bg-muted rounded text-sm">
                    Selected: <strong>{selectedStandardFiles.join(', ')}</strong>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="flex items-center justify-center gap-4">
            <Button
              variant="outline"
              onClick={() => setStatus('upload')}
              size="lg"
            >
              <Upload className="h-5 w-5 mr-2" />
              Upload New Files
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const handleDeleteStore = async (): Promise<void> => {
    // Try to get storeId from state or localStorage
    const storeIdToDelete = storeId || localStorage.getItem(getStoreIdKey());

    if (!storeIdToDelete) {
      toast({
        title: 'No Store Found',
        description: 'There is no store to delete.',
        variant: 'destructive',
      });
      return;
    }

    if (!confirm('Are you sure you want to delete the store and all uploaded files? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteRagStore(storeIdToDelete, ragService);
      localStorage.removeItem(getStoreIdKey());
      setStoreId(null);
      setUploadedFiles([]);
      hasLoadedFilesRef.current = false; // Reset flag after deletion
      setPolicyFile(null);
      setStandardFile(null);
      setSelectedPolicyFile(null);
      setSelectedStandardFiles([]);
      setQuery('');
      setOutput('');
      setIsStreaming(false);
      setInterimFindings([]);
      setStatus('upload');

      // Clear persisted output and status
      if (user?.email) {
        localStorage.removeItem(getOutputKey());
        localStorage.removeItem(getStatusKey());
      }

      toast({
        title: 'Store Deleted',
        description: 'The store and all files have been deleted.',
      });
    } catch (error) {
      console.error('Error deleting store:', error);
      toast({
        title: 'Delete Failed',
        description: error instanceof Error ? error.message : 'Failed to delete store',
        variant: 'destructive',
      });
    }
  };

  const generatePrintableHTML = (parsedData: {
    findings?: Array<{
      originalText?: string;
      revisedText?: string;
      reference?: string;
      complianceStatus?: string;
      severity?: string;
      findingType?: 'major' | 'minor';
      explanation?: string;
      analysis?: string;
      gaps?: string[];
      recommendations?: string[];
    }>;
    score?: {
      totalRequirements?: number;
      compliantRequirements?: number;
      nonCompliantRequirements?: number;
      missingRequirements?: number;
      needsClarification?: number;
      scoreBreakdown?: {
        critical?: number;
        high?: number;
        medium?: number;
        low?: number;
      };
      finalScore?: number;
      scoreExplanation?: string;
    };
    hasFindings?: boolean;
    message?: string;
  }): string => {
    const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <style>
          body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
          }
          .header {
            border-bottom: 2px solid #333;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .header h1 {
            margin: 0;
            font-size: 24px;
          }
          .header p {
            margin: 5px 0 0 0;
            color: #666;
          }
          .score-summary {
            background: #f5f5f5;
            border: 1px solid #ddd;
            border-radius: 8px;
            padding: 20px;
            margin-bottom: 30px;
          }
          .score-summary h2 {
            margin-top: 0;
            font-size: 20px;
          }
          .final-score {
            font-size: 48px;
            font-weight: bold;
            text-align: center;
            margin: 20px 0;
          }
          .score-breakdown {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 10px;
            margin-top: 15px;
          }
          .score-item {
            text-align: center;
            padding: 10px;
            background: white;
            border-radius: 4px;
          }
          .score-item strong {
            display: block;
            font-size: 18px;
            margin-top: 5px;
          }
          .message-banner {
            padding: 15px;
            border-radius: 8px;
            margin-bottom: 30px;
            background: #e8f5e9;
            border-left: 4px solid #4caf50;
          }
          .no-findings {
            text-align: center;
            padding: 40px;
            background: #e8f5e9;
            border-radius: 8px;
            margin-bottom: 30px;
          }
          .no-findings h3 {
            color: #2e7d32;
            margin-bottom: 10px;
          }
          .findings-section h2 {
            font-size: 20px;
            margin-bottom: 20px;
            border-bottom: 2px solid #333;
            padding-bottom: 10px;
          }
          .finding-card {
            border: 1px solid #ddd;
            border-radius: 8px;
            padding: 20px;
            margin-bottom: 20px;
            page-break-inside: avoid;
          }
          .finding-header {
            display: flex;
            justify-content: space-between;
            align-items: start;
            margin-bottom: 15px;
            border-bottom: 1px solid #eee;
            padding-bottom: 10px;
          }
          .finding-title {
            font-size: 18px;
            font-weight: bold;
            margin: 0;
          }
          .badges {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
          }
          .badge {
            padding: 4px 12px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: bold;
          }
          .badge-major {
            background: #ffebee;
            color: #c62828;
            border: 1px solid #c62828;
          }
          .badge-minor {
            background: #e3f2fd;
            color: #1565c0;
            border: 1px solid #1565c0;
          }
          .badge-critical {
            background: #ffebee;
            color: #c62828;
          }
          .badge-high {
            background: #fff3e0;
            color: #e65100;
          }
          .badge-medium {
            background: #fffde7;
            color: #f57f17;
          }
          .badge-low {
            background: #e3f2fd;
            color: #1565c0;
          }
          .field-section {
            margin-bottom: 15px;
          }
          .field-label {
            font-weight: bold;
            margin-bottom: 5px;
            color: #333;
          }
          .original-text {
            background: #ffebee;
            border-left: 4px solid #c62828;
            padding: 12px;
            border-radius: 4px;
            margin-top: 5px;
          }
          .revised-text {
            background: #e8f5e9;
            border-left: 4px solid #4caf50;
            padding: 12px;
            border-radius: 4px;
            margin-top: 5px;
          }
          .gaps-list, .recommendations-list {
            margin-top: 5px;
            padding-left: 20px;
          }
          .gaps-list li {
            color: #c62828;
            margin-bottom: 5px;
          }
          .recommendations-list li {
            color: #2e7d32;
            margin-bottom: 5px;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            text-align: center;
            color: #666;
            font-size: 12px;
          }
          @media print {
            body { margin: 0; padding: 15px; }
            .finding-card { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Compliance Audit Report</h1>
          <p>Generated on ${date}</p>
        </div>

        ${parsedData.score ? `
          <div class="score-summary">
            <h2>Compliance Score</h2>
            <div class="final-score">${parsedData.score.finalScore ?? 0}/100</div>
            ${parsedData.score.scoreBreakdown ? `
              <div class="score-breakdown">
                <div class="score-item">
                  <span>Critical</span>
                  <strong style="color: #c62828;">${parsedData.score.scoreBreakdown.critical ?? 0}</strong>
                </div>
                <div class="score-item">
                  <span>High</span>
                  <strong style="color: #e65100;">${parsedData.score.scoreBreakdown.high ?? 0}</strong>
                </div>
                <div class="score-item">
                  <span>Medium</span>
                  <strong style="color: #f57f17;">${parsedData.score.scoreBreakdown.medium ?? 0}</strong>
                </div>
                <div class="score-item">
                  <span>Low</span>
                  <strong style="color: #1565c0;">${parsedData.score.scoreBreakdown.low ?? 0}</strong>
                </div>
              </div>
            ` : ''}
            ${parsedData.score.scoreExplanation ? `
              <p style="margin-top: 15px; color: #666;">${parsedData.score.scoreExplanation}</p>
            ` : ''}
          </div>
        ` : ''}

        ${parsedData.message ? `
          <div class="message-banner">
            <strong>${parsedData.message}</strong>
          </div>
        ` : ''}

        ${(!parsedData.hasFindings || !parsedData.findings || parsedData.findings.length === 0) ? `
          <div class="no-findings">
            <h3>✓ No Compliance Issues Found</h3>
            <p>${parsedData.message || 'Policy is fully compliant with the standard.'}</p>
          </div>
        ` : `
          <div class="findings-section">
            <h2>Compliance Findings (${parsedData.findings.length})</h2>
            ${parsedData.findings.map((finding, idx) => `
              <div class="finding-card">
                <div class="finding-header">
                  <h3 class="finding-title">Finding #${idx + 1}</h3>
                  <div class="badges">
                    ${finding.findingType ? `
                      <span class="badge badge-${finding.findingType}">${finding.findingType.toUpperCase()}</span>
                    ` : ''}
                    ${finding.severity ? `
                      <span class="badge badge-${finding.severity}">${finding.severity.toUpperCase()}</span>
                    ` : ''}
                    ${finding.complianceStatus ? `
                      <span class="badge">${finding.complianceStatus.replace('-', ' ').toUpperCase()}</span>
                    ` : ''}
                  </div>
                </div>

                ${finding.explanation ? `
                  <div class="field-section">
                    <div class="field-label">Explanation</div>
                    <p>${finding.explanation}</p>
                  </div>
                ` : ''}

                <div class="field-section">
                  <div class="field-label">Original Text ${finding.originalText ? '(Non-Compliant)' : '(Missing Content)'}</div>
                  <div class="original-text">
                    ${finding.originalText ? `"${finding.originalText}"` : '<em>No existing text - content needs to be added</em>'}
                  </div>
                </div>

                ${finding.revisedText ? `
                  <div class="field-section">
                    <div class="field-label">Recommended Revision</div>
                    <div class="revised-text">"${finding.revisedText}"</div>
                  </div>
                ` : ''}

                ${finding.reference ? `
                  <div class="field-section">
                    <div class="field-label">Reference</div>
                    <p>${finding.reference}</p>
                  </div>
                ` : ''}

                ${finding.analysis ? `
                  <div class="field-section">
                    <div class="field-label">Analysis</div>
                    <p>${finding.analysis}</p>
                  </div>
                ` : ''}

                <div class="field-section">
                  <div class="field-label">Gaps</div>
                  ${finding.gaps && finding.gaps.length > 0 ? `
                    <ul class="gaps-list">
                      ${finding.gaps.map(gap => `<li>${gap}</li>`).join('')}
                    </ul>
                  ` : '<p><em>No specific gaps identified</em></p>'}
                </div>

                <div class="field-section">
                  <div class="field-label">Recommendations</div>
                  ${finding.recommendations && finding.recommendations.length > 0 ? `
                    <ul class="recommendations-list">
                      ${finding.recommendations.map(rec => `<li>${rec}</li>`).join('')}
                    </ul>
                  ` : '<p><em>No specific recommendations provided</em></p>'}
                </div>
              </div>
            `).join('')}
          </div>
        `}

        <div class="footer">
          <p>This report was generated automatically by the Compliance Audit System.</p>
        </div>
      </body>
      </html>
    `;
  };

  const handleExportPDF = async () => {
    if (!output) return;

    const element = document.getElementById('audit-results-content');
    if (!element) {
      toast({
        title: "Export Failed",
        description: "No audit results found to export.",
        variant: "destructive"
      });
      return;
    }

    // Clone the element to avoid modifying the live UI
    const clone = element.cloneNode(true) as HTMLElement;

    // Remove scroll restrictions and set full height on the clone
    clone.style.maxHeight = 'none';
    clone.style.overflow = 'visible';
    clone.style.height = 'auto';

    // Create a temporary container to hold the clone off-screen but rendered
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.top = '-9999px';
    container.style.left = '-9999px';
    container.style.width = '100%'; // Match width
    container.appendChild(clone);
    document.body.appendChild(container);

    const opt = {
      margin: 10,
      filename: 'compliance-audit-report.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };

    try {
      // @ts-ignore
      await html2pdf().set(opt).from(clone).save();
      toast({
        title: "PDF Exported",
        description: "Your report has been downloaded.",
      });
    } catch (error) {
      console.error('PDF export failed:', error);
      toast({
        title: "Export Failed",
        description: "Could not generate PDF.",
        variant: "destructive"
      });
    } finally {
      // Clean up
      document.body.removeChild(container);
    }
  };

  const renderAuditSection = () => (
    <div className="flex flex-col h-full space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Compliance Audit</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportPDF} disabled={!output}>
            <Download className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
          <Button variant="outline" onClick={handleNewChat}>
            <RotateCcw className="h-4 w-4 mr-2" />
            New Audit
          </Button>
          <Button
            variant="destructive"
            onClick={handleDeleteStore}
            disabled={!storeId && !localStorage.getItem(getStoreIdKey())}
          >
            <X className="h-4 w-4 mr-2" />
            Delete Store
          </Button>
        </div>
      </div>

      {isStreaming && (
        <Card className="mb-6 border-blue-500/20 bg-blue-500/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
              Live Analysis In Progress
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="font-medium text-blue-600 dark:text-blue-400">{streamingProgress.message}</span>
              </div>
              <Progress value={interimFindings.length > 0 ? (interimFindings.length % 20) * 5 : 5} className="h-2" />
            </div>

            {interimFindings.length > 0 && (
              <div className="mt-4 space-y-3">
                <h4 className="text-sm font-semibold">Interim Findings ({interimFindings.length})</h4>
                <div ref={interimFindingsScrollRef} className="max-h-[300px] overflow-y-auto space-y-3 pr-2">
                  {interimFindings.map((finding, idx) => {
                    // Create unique key from finding properties
                    const findingKey = `${finding.sectionId || finding.statementId || 'unknown'}-${finding.standardId}-${idx}-${finding.reference || 'no-ref'}`;
                    return (
                      <div key={findingKey} className="p-3 rounded-md bg-background border text-sm animate-in fade-in slide-in-from-top-2">
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-medium truncate max-w-[70%]">{finding.policyStatement}</span>
                          <Badge variant={
                            finding.complianceStatus === 'compliant' ? 'default' :
                              finding.complianceStatus === 'non-compliant' ? 'destructive' : 'secondary'
                          }>
                            {finding.complianceStatus}
                          </Badge>
                        </div>
                        <details className="group">
                          <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground list-none flex items-center gap-1">
                            <span>{finding.analysis?.substring(0, 100)}...</span>
                            <span className="text-blue-500 font-medium group-open:hidden">(Show More)</span>
                            <span className="text-blue-500 font-medium hidden group-open:inline">(Show Less)</span>
                          </summary>
                          <div className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap space-y-2">
                            {finding.reference && (
                              <div className="p-2 bg-muted/50 rounded border-l-2 border-blue-500">
                                <strong>Reference:</strong> {finding.reference}
                              </div>
                            )}

                            <div>
                              <strong>Analysis:</strong>
                              <div className="mt-1">{finding.analysis}</div>
                            </div>

                            {finding.gaps && finding.gaps.length > 0 && (
                              <div>
                                <strong>Gaps:</strong>
                                <ul className="list-disc list-inside pl-1 mt-1 text-red-500/80">
                                  {finding.gaps.map((gap, i) => (
                                    <li key={i}>{gap}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {finding.recommendations && finding.recommendations.length > 0 && (
                              <div>
                                <strong>Recommendations:</strong>
                                <ul className="list-disc list-inside pl-1 mt-1 text-green-600/80">
                                  {finding.recommendations.map((rec, i) => (
                                    <li key={i}>{rec}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </details>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!output && !isStreaming && (
        <Card>
          <CardContent className="py-8">
            <div className="flex items-center justify-center space-x-2">
              <Loader2 className="h-6 w-6 animate-spin" />
              <p className="text-muted-foreground">Running compliance audit...</p>
            </div>
          </CardContent>
        </Card>
      )}

      {output && (
        <Card>
          <CardHeader>
            <CardTitle>Audit Results</CardTitle>
          </CardHeader>
          <CardContent>
            <div id="audit-results-content" className="max-h-[600px] overflow-auto p-4">
              {renderFormattedAuditResults(output)}
            </div>
          </CardContent>
        </Card>
      )}

      {output && (
        <Card>
          <CardHeader>
            <CardTitle>Additional Query (Optional)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="query-input">Enter Additional Query</Label>
              <Textarea
                id="query-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Enter a follow-up query or additional question..."
                className="min-h-[100px]"
                disabled={isQueryLoading}
              />
              <p className="text-xs text-muted-foreground">
                Ask additional questions about the compliance audit results.
              </p>
            </div>

            <Button
              onClick={handleSubmitQuery}
              disabled={!storeId || isQueryLoading || !query.trim()}
              size="lg"
              className="w-full"
            >
              {isQueryLoading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" />
                  Submit Query
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {isQueryLoading && !output && (
        <Card>
          <CardContent className="py-8">
            <div className="flex items-center justify-center space-x-2">
              <Loader2 className="h-6 w-6 animate-spin" />
              <p className="text-muted-foreground">Processing audit...</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  return (
    <ScopingAILayout>
      <div className="container mx-auto px-4 py-5 md:py-6 space-y-5 md:space-y-6 overflow-x-hidden">
        <Button
          variant="ghost"
          onClick={() => navigate('/revisr')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        {/* RAG Service Selection */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Label className="text-sm font-medium">RAG Service:</Label>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="ragService"
                      value="gemini"
                      checked={ragService === 'gemini'}
                      onChange={(e) => setRagService(e.target.value as RAGService)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Gemini FileSearch</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="ragService"
                      value="langgraph"
                      checked={ragService === 'langgraph'}
                      onChange={(e) => setRagService(e.target.value as RAGService)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">LangGraph + Pinecone</span>
                  </label>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const currentStoreId = storeId || localStorage.getItem(getStoreIdKey());
                    if (currentStoreId) {
                      hasLoadedFilesRef.current = false; // Reset flag to allow manual refresh
                      loadUploadedFiles(currentStoreId);
                    } else {
                      toast({
                        title: 'No Store Found',
                        description: 'Please create a store by uploading files first.',
                        variant: 'destructive',
                      });
                    }
                  }}
                  disabled={isLoadingFiles}
                >
                  {isLoadingFiles ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Fetching...
                    </>
                  ) : (
                    <>
                      <FileText className="h-4 w-4 mr-2" />
                      Fetch Files
                    </>
                  )}
                </Button>
                <Badge variant={ragService === 'gemini' ? 'default' : 'secondary'}>
                  {ragService === 'gemini' ? 'Legacy' : 'New'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {isCheckingStore ? (
          <Card>
            <CardContent className="py-8">
              <div className="flex items-center justify-center space-x-2">
                <Loader2 className="h-6 w-6 animate-spin" />
                <p className="text-muted-foreground">Loading...</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {status === 'file-selection' && renderFileSelectionSection()}
            {status === 'upload' && renderUploadSection()}
            {status === 'processing' && renderProcessingSection()}
            {status === 'chatting' && renderAuditSection()}
          </>
        )}
      </div>
    </ScopingAILayout>
  );
};

export default QuickCompare;

